import { createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import axios from "axios";

import { CartItem } from "../../typings/interfaces/pos/cart-item";
import { ICartSession } from "../../typings/interfaces/pos/cart-session";
import { IReduxStore } from "../../typings/interfaces/reduxStore";
import {
  findBillItemLimitBreach,
  findPinTagCapacityBreach,
} from "../../util/bill-item-limit";
import { findPriceOverride } from "../../util/price-override";
import {
  BaseResponse,
  BaseResponseSingle,
  CategoryResponse,
  IDiscountResponse,
} from "../../typings";
import { PricingResponse } from "../../typings/interfaces/responses/pricing-response";
import {
  IBillItemRequest,
  IBillRequest,
} from "../../typings/interfaces/requests/new-bill-request";
import {
  ADD_PAYMENT,
  BILL_PAYMENT_HISTORY,
  CREATE_AND_UPDATE_BILL_NOTE,
  DELETE_BILL_NOTE,
  DELETE_CASHIER_DRAFTS,
  EDIT_BILL,
  GET_BILL_BY_ID,
  GET_BILL_ITEM_IDS,
  GET_BILL_NOTES_BY_ID,
  GET_CASHIER_DRAFTS,
  NEW_BILL,
  PICKUP_ITEM,
  SAVE_DRAFT,
  UPDATE_BILL_DISCOUNT,
} from "../../configs";
import {
  IBillResponse,
  IDraft,
} from "../../typings/interfaces/responses/new-bill-response";
import { BillNoteResponse } from "../../typings/interfaces/responses/bill-note-response";
import {
  createBillNoteRequest,
  updateBillNoteRequest,
} from "../../typings/interfaces/requests/bill-note-requests";
import { PaymentTypeEnum } from "../../typings/enum/paymentTypes";
import { IPaymentRequest } from "../../typings/interfaces/requests";
import {
  IBillPaymentHistory,
  PaymentCreateResponse,
} from "../../typings/interfaces/responses/paymentResponse";
import { useAppSelector } from "../../hooks";
import {
  IBillItemIdListResponse,
  IPickupItemResponse,
} from "../../typings/interfaces/responses/pickupResponses";
import { IPickupItemRequest } from "../../typings/interfaces/requests/pickupRequests";
import { serviceInvoiceCode } from "../../util/service-invoice-code";
import { IBillPaymentHistoryRequest } from "../../typings/interfaces/requests/bill-payment-historyRequest";
import dayjs from "dayjs";
import { PintagState } from "../slice/pintagSlice";
import { sessionSpecialCharge } from "../../util/special-charge";
import { ISpecialChargeConfig } from "../../typings/interfaces/responses/special-charge-response";

// Recomputes a session's pinTag to fit its current item count instead of
// blanking it out. Used by addCartItem/updateCartSessionItem/removeCartItem,
// which previously hard-reset pinTag to { color: null, tags: [], date: null }
// on every item add/remove/qty-change - including for a bill being edited
// that already had a real pin tag assigned, forcing the cashier to re-pick it
// from scratch every time. A session with no pin tag assigned yet (color
// null, no tags - the normal brand-new check-in case) is left untouched, so
// this is a no-op for that path. Uses the same
// total-item-count/service-tag-count denominator PinTagsSection's own
// cart-session-reading effect uses (pin-tags-section.tsx), so the resulting
// required slot count always matches what that effect - and the pin tag
// modal/drawer it drives - expects.
const resizePinTagForSession = (
  session: ICartSession,
  pintag: PintagState
): ICartSession["pinTag"] => {
  const pinTag = session.pinTag;
  const hasExistingTag =
    (pinTag?.color ?? null) !== null || (pinTag?.tags?.length ?? 0) > 0;

  if (!hasExistingTag) {
    return pinTag ?? { color: null, tags: [], date: null };
  }

  const totalItemCount = session.cartItems.reduce(
    (acc, item) => acc + (item.qty ?? 1),
    0
  );

  if (totalItemCount <= 0) {
    return { ...pinTag, tags: [] };
  }

  const serviceTypeId = session.cartItems[0]?.serviceType?.id;
  // Compare as numbers, as PinTagsSection does: serviceTypeId can arrive as a
  // string, and a miss fell back to one tag per item
  const servicePintag = pintag.getServicePintags?.body?.find(
    (sp) => Number(sp.serviceTypeId) === Number(serviceTypeId)
  );
  const serviceTagCount = servicePintag?.tagCount ?? 1;
  const requiredSlots = Math.max(
    1,
    Math.ceil(totalItemCount / serviceTagCount)
  );

  const existingTags = pinTag.tags ?? [];
  const resizedTags =
    requiredSlots >= existingTags.length
      ? [
          ...existingTags,
          ...Array(requiredSlots - existingTags.length).fill(""),
        ]
      : existingTags.slice(0, requiredSlots);

  return { ...pinTag, tags: resizedTags };
};

export const addSession = createAsyncThunk(
  "cart/addSession",
  async (sessionData: ICartSession, { rejectWithValue, getState }) => {
    try {
      const { cart } = getState() as IReduxStore;
      const copiedCart = cloneDeep(cart.cart);
      const sessionId = copiedCart.findIndex(
        (cartSession) => cartSession.sessionId === sessionData.sessionId
      );
      if (sessionId > -1) {
        copiedCart[sessionId] = sessionData;
      } else {
        copiedCart.push(sessionData);
      }

      return copiedCart;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const addCartItem = createAsyncThunk(
  "cart/addItem",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { cart, pintag, pricing, merchant } = getState() as IReduxStore;
      const copiedCartSessions = cloneDeep(cart.cart);
      const copiedCart = copiedCartSessions.findIndex(
        (cartItem) => cartItem.sessionId === cart.selectedSession?.sessionId
      );
      const itemId = copiedCartSessions[copiedCart].cartItems.findIndex(
        (cartSession) => cartSession.itemId === cart.selectedCartItem?.itemId
      );

      copiedCartSessions[copiedCart].cartItems[itemId].finalized = true;
      // An edited ticket's pin tags come first, then the service max
      const pinTagBreach = findPinTagCapacityBreach(
        cart.cart[copiedCart],
        copiedCartSessions[copiedCart],
        cart.editPinTagCapacity,
        pintag.getServicePintags?.body,
        pricing.categoryResponse?.body
      );
      if (pinTagBreach) return rejectWithValue(pinTagBreach);
      const limitBreach = findBillItemLimitBreach(
        cart.cart[copiedCart],
        copiedCartSessions[copiedCart],
        pricing.categoryResponse?.body,
        merchant.merchantListResponse?.body?.[0]?.defaultMaxItemsPerBill
      );
      if (limitBreach) return rejectWithValue(limitBreach);
      copiedCartSessions[copiedCart].pinTag = resizePinTagForSession(
        copiedCartSessions[copiedCart],
        pintag
      );
      const cartSession = copiedCartSessions[copiedCart];

      return { copiedCartSessions, cartSession };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const removeSession = createAsyncThunk(
  "cart/removeSession",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { cart } = getState() as IReduxStore;
      if (cart.cart.length === 1) {
        return cart.cart;
      }
      const filteredSession = cart.cart.filter(
        (session) => session.sessionId !== cart.selectedSession?.sessionId
      );
      return filteredSession;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateCartSessionItem = createAsyncThunk(
  "cart/updateCartSessionItem",
  async (
    { cartItem }: { cartItem: CartItem },
    { rejectWithValue, getState }
  ) => {
    try {
      const { cart, pricing, priceOverrideConfig, pintag, merchant } =
        getState() as IReduxStore;
      const clonedCart = cloneDeep(cart.cart);
      const selectedSessionIndex = clonedCart.findIndex(
        (session) => session.sessionId === cart.selectedSession?.sessionId
      );

      // Check whether if item in cart selected or not
      const updateItem = () => {
        const subCategoryId = cartItem.garmentType?.id || 0;
        const secondSubCategory = cartItem.garmentStyle?.id || 0;
        const fabricType = cartItem.materialType?.id || 0;
        // The item's own service, not the open tab: an item already in the
        // cart (e.g. removing Press Only while editing a mixed bill with
        // another tab open) must be repriced from its own service's prices.
        const category = cartItem.serviceType?.id ?? cart.selectedServiceType?.id;

        const dcPrice = pricing.dryCleanResponse?.body.filter(
          (item: PricingResponse) => {
            return (
              item.categoryId ===
              (category === -100000 ? cartItem.serviceType?.id : category) &&
              item.subCategoryId === subCategoryId &&
              item.secondSubCategoryId === secondSubCategory &&
              item.materialId === fabricType
            );
          }
        );

        let price = (dcPrice && dcPrice[0]?.price) || 0;

        // Apply price override if applicable
        const activeConfigs = priceOverrideConfig.activeConfigs || [];
        const categoryId = category === -100000 ? cartItem.serviceType?.id : category;

        const applicableConfig = findPriceOverride(activeConfigs, categoryId, price, cartItem);

        const listPrice = price;
        if (applicableConfig) {
          if (applicableConfig.reduceAmountType === "FIXED") {
            price = Math.max(0, price - applicableConfig.reduceAmount);
          } else if (applicableConfig.reduceAmountType === "PERCENTAGE") {
            price = Math.max(0, price - (price * applicableConfig.reduceAmount / 100));
          }
        }

        if (!cartItem.isPriceEdited) {
          cartItem.unitPrice = price;
          // Press Only / Clean Only is its own (cheaper) service: keep the
          // regular price so the cart and receipts can show it struck through.
          const isProcessingOverride =
            applicableConfig?.processingType === "PRESS_ONLY" ||
            applicableConfig?.processingType === "CLEAN_ONLY";
          cartItem.baseUnitPrice =
            isProcessingOverride && price < listPrice ? listPrice : undefined;
        }

        const sessionItems =
          clonedCart[selectedSessionIndex].cartItems.map((crtItem) => {
            // Match the item being updated, not the selected one: removing a
            // Redo / Press Only / Clean Only tag on another row would otherwise
            // overwrite the selected item with a copy of that row.
            return crtItem.itemId === cartItem.itemId
              ? cartItem
              : crtItem;
          }) || [];
        clonedCart[selectedSessionIndex].cartItems = sessionItems;
      };

      if (cart.selectedCartItem && cart.selectedCartItem.finalized === false) {
        updateItem();
      } else {
        const isItemAlreadyExists = clonedCart[
          selectedSessionIndex
        ].cartItems.find((item: any) => item.itemId === cartItem.itemId);

        if (isItemAlreadyExists === undefined) {
          clonedCart[selectedSessionIndex].cartItems = [
            ...clonedCart[selectedSessionIndex].cartItems,
            cartItem,
          ];
        } else {
          updateItem();
        }
      }
      const pinTagBreach = findPinTagCapacityBreach(
        cart.cart[selectedSessionIndex],
        clonedCart[selectedSessionIndex],
        cart.editPinTagCapacity,
        pintag.getServicePintags?.body,
        pricing.categoryResponse?.body
      );
      if (pinTagBreach) return rejectWithValue(pinTagBreach);
      const limitBreach = findBillItemLimitBreach(
        cart.cart[selectedSessionIndex],
        clonedCart[selectedSessionIndex],
        pricing.categoryResponse?.body,
        merchant.merchantListResponse?.body?.[0]?.defaultMaxItemsPerBill
      );
      if (limitBreach) return rejectWithValue(limitBreach);

      const previousItem = cart.cart[selectedSessionIndex]?.cartItems.find(
        (item) => item.itemId === cartItem.itemId
      );
      if (previousItem && previousItem.qty !== cartItem.qty) {
        clonedCart[selectedSessionIndex].pinTag = resizePinTagForSession(
          clonedCart[selectedSessionIndex],
          pintag
        );
      }

      const session = clonedCart[selectedSessionIndex];

      return { clonedCart, cartItem, session };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { cart, pintag } = getState() as IReduxStore;
      const clonedCart = cloneDeep(cart.cart);
      const sessionIndex = clonedCart.findIndex(
        (session) => session.sessionId === cart.selectedSession?.sessionId
      );
      const filteredItems = clonedCart[sessionIndex]?.cartItems.filter(
        (item) => item.itemId !== cart.selectedCartItem?.itemId
      );
      clonedCart[sessionIndex].cartItems = filteredItems || [];
      clonedCart[sessionIndex].pinTag = resizePinTagForSession(
        clonedCart[sessionIndex],
        pintag
      );
      const session = clonedCart[sessionIndex];
      return { clonedCart, session };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// export const newBillAction = createAsyncThunk(
//   "cart/newBill",
//   async (
//     { tenant, paymentType }: { tenant: string; paymentType: PaymentTypeEnum },
//     { rejectWithValue, getState }
//   ) => {
//     try {
//       const { cart, auth, customer } = getState() as IReduxStore;
//       let billItemList: IBillItemRequest[] = [];
//       cart.cart.forEach(async (session) => {
//         const bill: IBillRequest = {
//           billItemList: [],
//           serviceTypeId: "1",
//           merchantId: tenant,
//           cashierId: `${auth.user?.id}`,
//           customerId: customer.customer?.id || 0,
//           paymentType: paymentType,
//           billString: JSON.stringify(session),
//         };

//         session.cartItems.forEach((item) => {
//           billItemList = [];
//           const billItem: IBillItemRequest = {
//             firstCategoryId: `${item.garmentType?.id}`,
//             secondCategoryId: `${item.materialType}`,
//             secondSubCategoryId: `${item.garmentStyle?.id}`,
//             itemPrice: item.unitPrice,
//             itemQty: item.qty,
//             total: item.unitPrice * item.qty,
//             color: item.garmentColor as string[],
//             textureType: item.texture as string,
//             instructions: item.instructions,
//             alterationFirstCategoryId: `${item.garmentType?.id}`,
//             alterationSecondCategoryId: `${item.alterationSubCategory?.id}`,
//             alterationSecondSubCategoryId: `${item.alterationSecondSubCategory?.id}`,
//           };
//           billItemList.push(billItem);
//         });
//         bill.billItemList = billItemList;

//         const response = await axios.post<BaseResponseSingle<IBillResponse>>(
//           NEW_BILL,
//           bill
//         );
//         return response.data.body;
//       });
//     } catch (err) {
//       return rejectWithValue(err);
//     }
//   }
// );

interface INewBillAction extends ICartSession {
  discount: {
    id: number | null;
    amount: number;
    // The discount as applied, saved in billString so editing the invoice
    // can show it and keep it unchanged.
    details?: IDiscountResponse | null;
  };
  pickupDetails: {
    isSpecial: boolean;
    dueDate: string;
  };
}

// The pin tag holds only the ready date; the ready time (store pickup time,
// e.g. 3pm) comes from the check-in summary. Sent as local wall-clock time,
// as before, so the saved date doesn't shift.
const readyAt = (date: string, readyTime?: string | null) => {
  const time = readyTime ? dayjs(readyTime) : null;
  return dayjs(date)
    .hour(time?.isValid() ? time.hour() : 0)
    .minute(time?.isValid() ? time.minute() : 0)
    .second(0)
    .millisecond(0)
    .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
};

// Saves each item's Special charge on it (0 on a Regular ticket), so the
// bill, its billString and later pickups/reprints all use the same figure.
const withSpecialCharges = (
  session: INewBillAction,
  rules: ISpecialChargeConfig[] | null
): INewBillAction => {
  const isSpecial = session.pickupDetails?.isSpecial ?? false;
  return {
    ...session,
    isSpecial,
    cartItems: session.cartItems.map((item) => ({
      ...item,
      specialCharge: sessionSpecialCharge(item, isSpecial, rules),
    })),
  };
};

export const newBillAction = createAsyncThunk(
  "cart/newBill",
  async (
    {
      tenant,
      sessionList,
    }: // discount,
      {
        tenant: string;
        paymentType: PaymentTypeEnum;
        sessionList: INewBillAction[];
        // discount: { id: number | null; amount: number } | null;
      },
    { rejectWithValue, getState }
  ) => {
    try {
      const { cart, auth, customer, pintag, pricing, specialCharge } = getState() as IReduxStore;
      const { getColorPintags } = pintag;

      if (!auth || !auth.user) {
        return rejectWithValue("Cashier not found");
      }
      if (!customer || !customer.customer) {
        return rejectWithValue("Customer not found");
      }

      const stationAccessSessionId = localStorage.getItem(
        "stationAccessSessionId"
      );
      const activeCashierSession = localStorage.getItem("activeCashierSession");
      let parsedSession: { cashierPointId: number } | null = null;
      if (activeCashierSession) {
        try {
          parsedSession = JSON.parse(activeCashierSession);
        } catch (err) {
          return rejectWithValue("Invalid activeCashierSession format");
        }
      }

      const resolveColorAlias = (input: string | null | undefined): string => {
        // console.log("resolveColorAlias called with color:", input);
        if (!input) {
          // console.warn("Pin color missing, using fallback alias.");
          return "NA";
        }

        const normalizedInput = input.trim().toLowerCase();

        // Use the dynamic list from the store
        const pinTagsList = getColorPintags?.body || [];

        const match = pinTagsList.find(
          (item) =>
            item.color?.toLowerCase() === normalizedInput ||
            item.alias?.toLowerCase() === normalizedInput
        );

        if (!match) {
          // Note: If the backend accepts the color code itself when no alias matches, 
          // you might want to return `input` or `normalizedInput` here depending on backend logic.
          // But adhering to the previous logic of searching for a match, we'll warn.
          console.warn(`No match found for color or alias: ${input}`);
        }

        return match?.alias ?? "NA";
      };

      const preparedSessionList: IBillRequest[] = sessionList.map((unpricedSession) => {
        const session = withSpecialCharges(unpricedSession, specialCharge.configs);
        console.log(session);
        return {
          sessionId: session.sessionId,
          billItemList: session.cartItems.map((cartItem) => {
            const prep: IBillItemRequest = {
              firstCategoryId: String(cartItem.garmentType!.id),
              secondCategoryId: String(cartItem.materialType!.id),
              secondSubCategoryId: String(cartItem.garmentStyle!.id),
              itemPrice: cartItem.redoDetails || cartItem.alterationOnly ? 0 : cartItem.unitPrice,
              itemQty: cartItem.qty,
              total: cartItem.redoDetails
                ? 0
                : (cartItem.alterationOnly ? 0 : cartItem.unitPrice * cartItem.qty) +
                cartItem.alterationItems.reduce(
                  (acc, alt) => acc + (alt.unitPrice ?? 0),
                  0
                ),
              alterationTotal: cartItem.redoDetails
                ? 0
                : cartItem.alterationItems.reduce(
                  (acc, alt) => acc + (alt.unitPrice ?? 0),
                  0
                ),
              specialCharge: cartItem.specialCharge ?? 0,
              color: cartItem.garmentColor as string[],
              textureType: cartItem.texture ?? null,
              instructions: cartItem.instructions.map(
                (instruction) => instruction.instruction
              ),
              alterationFirstCategoryId: cartItem.garmentType
                ? String(cartItem.garmentType.id)
                : null,
              alterationSecondCategoryId: cartItem.alterationSubCategory
                ? String(cartItem.alterationSubCategory.id)
                : null,
              alterationSecondSubCategoryId:
                cartItem.alterationSecondSubCategory
                  ? String(cartItem.alterationSecondSubCategory.id)
                  : null,
              hashId: cartItem.itemId,
              isRedo: !!cartItem.redoDetails,
              redoTicketNo: cartItem.redoDetails?.ticketNo,
              redoReason: cartItem.redoDetails?.reason,
              redoComment: cartItem.redoDetails?.comment,
              processType: cartItem.pressOnly ? "PRESS_ONLY" : cartItem.cleanOnly ? "CLEAN_ONLY" : cartItem.alterationOnly ? "ALTERATION_ONLY" : "REGULAR",
              instruction: cartItem.instructions.map((instruction) => {
                return {
                  id: instruction.id,
                  instruction: instruction.instruction,
                };
              }),
              damage: cartItem.damages.map((damage) => {
                return {
                  id: damage.id,
                  damage: damage.damage,
                };
              }),
              release: (cartItem.releases ?? []).map((release) => ({
                id: release.id,
                name: release.name,
              })),
              spot: cartItem.spots.map((spot) => {
                return {
                  id: spot.id,
                  spot: spot.label,
                  type: spot.subSpot.comment,
                  price: cartItem.redoDetails ? 0 : spot.subSpot.price ?? 0,
                };
              }),
              upcharge: cartItem.upcharges.map((upcharge) => {
                return {
                  id: upcharge.id,
                  upcharge: upcharge.label,
                  upchargeVariants: [
                    {
                      id: upcharge.subUpcharge.id,
                      upchargeVariant: upcharge.subUpcharge.upchargeVariant,
                      price: cartItem.redoDetails ? 0 : upcharge.subUpcharge.price,
                    },
                  ],
                };
              }),
            };
            return prep;
          }),
          serviceTypeId: String(session.cartItems[0].serviceType!.id),
          serviceTypeCode: serviceInvoiceCode(
            session.cartItems[0].serviceType,
            pricing.categoryResponse?.body
          ),
          merchantId: tenant,
          cashierId: String(auth.user!.id),
          customerId: customer.customer!.id,
          paymentType: PaymentTypeEnum.CASH,
          billString: JSON.stringify(session),
          discountAmount: Number((session.discount.amount || 0).toFixed(2)),
          discountId: session.discount.id,
          stationAccessSessionId: Number(stationAccessSessionId),
          cashierPointId: parsedSession?.cashierPointId,
          pinTag: {
            color: resolveColorAlias(session.pinTag?.color ?? ""),
            tags: session.pinTag?.tags?.filter(Boolean) ?? [],
          },

          pickupDetails: {
            isSpecial: session.pickupDetails?.isSpecial ?? false,
            dueDate: session.pinTag?.date
              ? readyAt(session.pinTag.date, session.pickupDetails?.dueDate)
              : "",
          },
        };
      });

      const response = await axios.post<BaseResponse<IBillResponse>>(
        NEW_BILL,
        preparedSessionList
      );
      // The BE reports failures as HTTP 200 + status FAILED with a body of null
      if (response.data.status !== "SUCCESS") {
        return rejectWithValue(response.data.message || "Bill could not be created");
      }

      // cart?.temporaryBillNotes?.forEach(async (tempNote) => {
      //   if (tempNote.sessionId === session.sessionId) {
      //   }
      // });
      // }

      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getBillByIdAction = createAsyncThunk(
  "cart/getBillById",
  async ({ billId }: { billId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponseSingle<IBillResponse>>(
        GET_BILL_BY_ID(billId)
      );
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
/* 
export const addUserNoteAction = createAsyncThunk(
  "customer/create-note",
  async (
    { note, occasions, merchantUserId, customerId }: CreateUserNoteRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponse<CreateUserNoteRequest>>(
        CREATE_AND_UPDATE_USER_NOTE,
        {
          note,
          occasions,
          merchantUserId,
          customerId,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

*/
export const addBillNoteAction = createAsyncThunk(
  "cart/addBillNote",
  async (
    {
      note,
      occasions,
      merchantUserId,
      customerId,
      billId,
    }: createBillNoteRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post<BaseResponse<createBillNoteRequest>>(
        CREATE_AND_UPDATE_BILL_NOTE,
        {
          note,
          occasions,
          merchantUserId,
          customerId,
          billId,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getBillNoteByIdAction = createAsyncThunk(
  "cart/getBillNoteById",
  async ({ billId }: { billId: number }, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<BillNoteResponse>>(
        GET_BILL_NOTES_BY_ID(billId)
      );
      let sorted = response.data.body?.sort(
        (a: any, b: any) =>
          new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime()
      );
      return sorted;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const updateBillNoteAction = createAsyncThunk(
  "cart/updateBillNote",
  async (
    {
      id,
      note,
      occasions,
      merchantUserId,
      customerId,
      billId,
    }: updateBillNoteRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<BaseResponse<updateBillNoteRequest>>(
        CREATE_AND_UPDATE_BILL_NOTE,
        {
          id,
          note,
          occasions,
          merchantUserId,
          customerId,
          billId,
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteBillNoteAction = createAsyncThunk(
  "cart/deleteBillNote",
  async (noteId: string, { rejectWithValue }) => {
    try {
      const response = await axios.delete<BaseResponse<null>>(
        DELETE_BILL_NOTE(noteId)
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const newPaymentAction = createAsyncThunk(
  "cart/newPayment",
  async (newPayment: IPaymentRequest, { rejectWithValue }) => {
    console.log("data", newPayment);
    try {
      const response = await axios.post<BaseResponse<PaymentCreateResponse>>(
        ADD_PAYMENT,
        newPayment
      );
      console.log("response", response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const pickUpItemAction = createAsyncThunk(
  "cart/pickUpItem",
  async (data: IPickupItemRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<IPickupItemResponse>
      >(PICKUP_ITEM, data);
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getBillItemIdListAction = createAsyncThunk(
  "cart/getBillsIdList",
  async (billId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get<BaseResponse<IBillItemIdListResponse>>(
        GET_BILL_ITEM_IDS(billId)
      );
      const prep: {
        billId: number;
        billItemIdList: IBillItemIdListResponse[];
      } = {
        billId: billId,
        billItemIdList: response.data.body,
      };
      return prep;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getBillPaymentHistoryAction = createAsyncThunk(
  "bill/getBillPaymentHistory",
  async (request: IBillPaymentHistoryRequest, { rejectWithValue }) => {
    try {
      const response = await axios.post<
        BaseResponseSingle<IBillPaymentHistory>
      >(BILL_PAYMENT_HISTORY, request);
      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// export const editBillAction = createAsyncThunk(
//   "cart/editBill",
//   async (req: IBillRequest & { id: number }, { rejectWithValue }) => {
//     try {
//       const response = await axios.put<BaseResponseSingle<IBillResponse>>(
//         EDIT_BILL(req.id),
//         req
//       );
//       return response.data.body;
//     } catch (err) {
//       return rejectWithValue(err);
//     }
//   }
// );

export const editBillAction = createAsyncThunk(
  "cart/editBill",
  async (
    {
      tenant,
      session: unpricedSession,
      // billId,
      id,
    }: // discount,
      {
        tenant: string;
        paymentType: PaymentTypeEnum;
        session: INewBillAction;
        // billId: string;
        id: number;
        // discount: { id: number | null; amount: number } | null;
      },
    { rejectWithValue, getState }
  ) => {
    try {
      const { cart, auth, customer, pintag, pricing, specialCharge } = getState() as IReduxStore;
      const session = withSpecialCharges(unpricedSession, specialCharge.configs);

      if (!auth || !auth.user) {
        return rejectWithValue("Cashier not found");
      }
      if (!customer || !customer.customer) {
        return rejectWithValue("Customer not found");
      }

      const stationAccessSessionId = localStorage.getItem(
        "stationAccessSessionId"
      );
      const activeCashierSession = localStorage.getItem("activeCashierSession");
      let parsedSession: { cashierPointId: number } | null = null;
      if (activeCashierSession) {
        try {
          parsedSession = JSON.parse(activeCashierSession);
        } catch (err) {
          return rejectWithValue("Invalid activeCashierSession format");
        }
      }

      // session.pinTag.color may hold either the palette's hex color or its
      // alias, depending on which UI path last wrote it (see
      // pintagAction.ts's updateBillPintagAction comment) - the backend's
      // PintagDto.color is looked up via findByAlias, so it must always be
      // the alias. Resolve it here the same way newBillAction and
      // updateBillPintagAction already do, instead of forwarding it verbatim.
      let resolvedPintagColor = session.pinTag?.color ?? "";
      if (resolvedPintagColor) {
        const normalizedColor = resolvedPintagColor.trim().toLowerCase();
        const colorPintags = pintag?.getColorPintags?.body ?? [];
        const matched = colorPintags.find(
          (item) =>
            item.color?.toLowerCase() === normalizedColor ||
            item.alias?.toLowerCase() === normalizedColor
        );
        if (!matched) {
          return rejectWithValue(
            `Unable to resolve pin tag color "${resolvedPintagColor}". Please refresh and try again.`
          );
        }
        resolvedPintagColor = matched.alias;
      }

      const preparedSessionList: IBillRequest = {
        // billId: billId,
        id: id,
        sessionId: session.sessionId,
        billItemList: session.cartItems.map((cartItem) => {
          const prep: IBillItemRequest = {
            firstCategoryId: String(cartItem.garmentType!.id),
            secondCategoryId: String(cartItem.materialType!.id),
            secondSubCategoryId: String(cartItem.garmentStyle!.id),
            itemPrice: cartItem.redoDetails || cartItem.alterationOnly ? 0 : cartItem.unitPrice,
            itemQty: cartItem.qty,
            total: cartItem.redoDetails
              ? 0
              : (cartItem.alterationOnly ? 0 : cartItem.unitPrice * cartItem.qty) +
              cartItem.alterationItems.reduce(
                (acc, alt) => acc + (alt.unitPrice ?? 0),
                0
              ),
            alterationTotal: cartItem.redoDetails
              ? 0
              : cartItem.alterationItems.reduce(
                (acc, alt) => acc + (alt.unitPrice ?? 0),
                0
              ),
            specialCharge: cartItem.specialCharge ?? 0,
            color: cartItem.garmentColor as string[],
            textureType: cartItem.texture ?? null,
            instructions: cartItem.instructions.map(
              (instruction) => instruction.instruction
            ),
            alterationFirstCategoryId: cartItem.garmentType
              ? String(cartItem.garmentType.id)
              : null,
            alterationSecondCategoryId: cartItem.alterationSubCategory
              ? String(cartItem.alterationSubCategory.id)
              : null,
            alterationSecondSubCategoryId: cartItem.alterationSecondSubCategory
              ? String(cartItem.alterationSecondSubCategory.id)
              : null,
            hashId: cartItem.itemId,
            isRedo: !!cartItem.redoDetails,
            redoTicketNo: cartItem.redoDetails?.ticketNo,
            redoReason: cartItem.redoDetails?.reason,
            redoComment: cartItem.redoDetails?.comment,
            processType: cartItem.pressOnly ? "PRESS_ONLY" : cartItem.cleanOnly ? "CLEAN_ONLY" : cartItem.alterationOnly ? "ALTERATION_ONLY" : "REGULAR",
            instruction: cartItem.instructions.map((instruction) => {
              return {
                id: instruction.id,
                instruction: instruction.instruction,
              };
            }),
            damage: cartItem.damages.map((damage) => {
              return {
                id: damage.id,
                damage: damage.damage,
              };
            }),
            release: (cartItem.releases ?? []).map((release) => ({
              id: release.id,
              name: release.name,
            })),
            spot: cartItem.spots.map((spot) => {
              return {
                id: spot.id,
                spot: spot.label,
                type: spot.subSpot.comment,
                price: cartItem.redoDetails ? 0 : spot.subSpot.price ?? 0,
              };
            }),
            upcharge: cartItem.upcharges.map((upcharge) => {
              return {
                id: upcharge.id,
                upcharge: upcharge.label,
                upchargeVariants: [
                  {
                    id: upcharge.subUpcharge.id,
                    upchargeVariant: upcharge.subUpcharge.upchargeVariant,
                    price: cartItem.redoDetails ? 0 : upcharge.subUpcharge.price,
                  },
                ],
              };
            }),
          };
          return prep;
        }),
        serviceTypeId: String(session.cartItems[0].serviceType!.id),
        serviceTypeCode: serviceInvoiceCode(
          session.cartItems[0].serviceType,
          pricing.categoryResponse?.body
        ),
        merchantId: tenant,
        cashierId: String(auth.user!.id),
        customerId: customer.customer!.id,
        paymentType: PaymentTypeEnum.CASH,
        billString: JSON.stringify(session),
        discountAmount: Number((session.discount.amount || 0).toFixed(2)),
        discountId: session.discount.id,
        pinTag: {
          color: resolvedPintagColor,
          tags: session.pinTag.tags,
        },
        pickupDetails: session.pickupDetails,
        stationAccessSessionId: Number(stationAccessSessionId),
        cashierPointId: parsedSession?.cashierPointId,
      };

      const response = await axios.put<BaseResponseSingle<IBillResponse>>(
        EDIT_BILL(id),
        preparedSessionList
      );

      return response.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Narrow counterpart to editBillAction, for screens (e.g. Pickup) that only
// have a possibly-stale cached snapshot of the bill's items and must not
// resend/replace the full billItemList. Only touches discountAmount/discountId
// (and the recomputed billAmount that comes back from the backend).
export const updateBillDiscountAction = createAsyncThunk(
  "cart/updateBillDiscount",
  async (
    {
      billId,
      discountAmount,
      discountId,
      checkout,
    }: {
      billId: number;
      discountAmount: number;
      discountId: number | null;
      // true: a checkout discount, stacked on the bill's check-in discount
      checkout?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.put<BaseResponseSingle<IBillResponse>>(
        UPDATE_BILL_DISCOUNT(billId),
        { discountAmount, discountId, checkout }
      );

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data?.message || "Failed to update discount"
        );
      }

      return response.data.body;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || err?.message || "Failed to update discount"
      );
    }
  }
);

const safeStringify = (obj: any) =>
  JSON.stringify(obj, (_k, v) => (v instanceof Date ? v.toISOString() : v));

const toBillStrings = (input: any | any[]): string[] => {
  const arr = Array.isArray(input) ? input : [input];
  return arr
    .filter(Boolean)
    .map((b) => (typeof b === "string" ? b : safeStringify(b)));
};

export const saveDraftAction = createAsyncThunk<
  any,
  {
    tenantId: number;
    storeId: number;
    cashierId: number;
    customerId: number;
    bills: any | any[];
  }
>(
  "cart/saveDraft",
  async (
    { tenantId, storeId, cashierId, customerId, bills },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        tenantId,
        storeId,
        cashierId,
        customerId,
        bills: toBillStrings(bills),
      };

      const res = await axios.post<BaseResponseSingle<any>>(
        SAVE_DRAFT,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return res.data.body;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const itemsFromBillString = (billString: string) => {
  try {
    const o = JSON.parse(billString) as {
      draftSessions?: { cartItems?: any[] }[];
    };
    return o.draftSessions?.flatMap((s) => s.cartItems ?? []) ?? [];
  } catch {
    return [];
  }
};

export const parseDraftString = (
  billString: string,
  opts?: { draftIdOverride?: number; preserveInnerIdAs?: "innerDraftId" }
) => {
  const o = JSON.parse(billString);
  const draftSessions = Array.isArray(o.draftSessions) ? o.draftSessions : [];

  const base = {
    draftId: opts?.draftIdOverride ?? o.draftId ?? 0,
    customer: o.customer,
    draftSessions,
    createdAt: o.createdAt ? new Date(o.createdAt) : new Date(),
    totalPieces:
      o.totalPieces ??
      draftSessions.reduce(
        (a: number, s: any) =>
          a +
          (s.cartItems?.reduce(
            (x: number, i: any) =>
              x + (i.qty ?? 0) * (i.garmentStyle?.pieceCount ?? 1),
            0
          ) ?? 0),
        0
      ),
    totalAmount:
      o.totalAmount ??
      draftSessions.reduce(
        (a: number, s: any) =>
          a +
          (s.cartItems?.reduce(
            (x: number, i: any) => x + (i.qty ?? 0) * (i.unitPrice ?? 0),
            0
          ) ?? 0),
        0
      ),
  } as any;

  if (opts?.preserveInnerIdAs && o.draftId != null) {
    base[opts.preserveInnerIdAs] = o.draftId;
  }
  return base as IDraft;
};

interface ServerDraftRow {
  id: number;
  tenantId: number;
  storeId: number;
  cashierId: number;
  billString: string;
  customerId: number;
}

interface ServerDraftEnvelope {
  id: number;
  tenantId: number;
  storeId: number;
  cashierId: number;
  customerId: number;
  draftBills: ServerDraftRow[];
}

export const getCashierDraftsAction = createAsyncThunk<IDraft[], number>(
  "cart/getCashierDrafts",
  async (cashierId, { rejectWithValue }) => {
    try {
      const res = await axios.get<BaseResponse<ServerDraftEnvelope>>(
        GET_CASHIER_DRAFTS(cashierId)
      );

      const envelopes: ServerDraftEnvelope[] = res.data.body ?? [];

      const drafts: IDraft[] = envelopes
        .flatMap((env: ServerDraftEnvelope) =>
          (env.draftBills ?? []).map((row: ServerDraftRow) => {
            try {
              const parsed = parseDraftString(row.billString, {
                draftIdOverride: env.id,
                preserveInnerIdAs: "innerDraftId",
              });

              (parsed as any).serverRowId = row.id;
              return parsed;
            } catch {
              return null;
            }
          })
        )
        .filter((d): d is IDraft => Boolean(d));

      return drafts;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteDraftAction = createAsyncThunk<number, number>(
  "cart/deleteDraft",
  async (billId, { rejectWithValue }) => {
    try {
      await axios.delete<BaseResponse<null>>(DELETE_CASHIER_DRAFTS(billId));
      return billId;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
